import FilledButtonM from "@common/components/button/FilledButtonM"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { useInterval } from "@common/utils/useInterval"
import { pauseActivity } from "@data/redux/actions/activity"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import {
    activityOfflineModeInvalid,
    activityStartedWithOfflineMode,
    setIsInternetReachable,
    setIsOfflineModeEnabled,
    timerStepStarted,
} from "@data/redux/actions/offlineMode"
import { activitiesService } from "@data/services/activitiesService"
import {
    getElapsedTime,
    recordStartTime,
} from "@data/utility/asyncStorageUtils"
import { useNavigation } from "@react-navigation/native"
import LottieView from "lottie-react-native"
import React, { useContext, useEffect, useRef, useState } from "react"
import { useController } from "react-hook-form"
import { AppState, Dimensions, Platform, View } from "react-native"
import CircularProgress from "react-native-circular-progress-indicator/src/circularProgress"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import { setTimerState } from "../../../data/redux/actions/activity"
import ChampionBubble from "../../champions/ChampionBubble"
import OneStepActivityHeader from "../one-step/OneStepActivityHeader"
import { undefinedMaterialSlug } from "../todo/ActivityPicker"
import MaterialModal from "./MaterialDisplayModal"
import { hasRole } from "@data/utility/ability"

const TimerActivityStep = (props) => {
    // ** Component props
    const {
        control,
        status,
        statusRef,
        setStatus,
        activity,
        setChronoStepFinishedComment = () => {},
        animationFinished,
        setAnimationFinished,
        submitted,
        globalProgress,
        submittedMessage,
        stepDescriptions,
        setChronoDuration,
        onExit,
        route,
        hideStartAnimation = false,
        isInCheckpointOverlay = false,
        hasQuizOverlay = false,
    } = props

    const navigation = useNavigation()
    const [isOpen, setOpen] = useState(false)
    const handleShowModal = () => setOpen(!isOpen)

    const activityMaterial = activity?.material
    const uMaterialSlug = undefinedMaterialSlug(activity?.slug)

    // ** Time elapsed in seconds
    const { time: stateTime, seenLottie } = props.timers[activity?.id] ?? {
        time: activity?.duration,
        seenLottie: true,
    }

    const time = stateTime ?? 0

    const setTime = (data) => {
        props.setTimerState(activity?.id, data, animationFinished)
        setChronoDuration(data)
    }

    const setSeenLottie = (data) => {
        props.setTimerState(activity?.id, time, data)
    }

    const finishAnimation = () => {
        setAnimationFinished(true)
        setSeenLottie(true)
        PlaySoundM.stop()
    }

    // Skip animation if hideStartAnimation is true or quiz overlay is active
    useEffect(() => {
        if ((hideStartAnimation || hasQuizOverlay) && !animationFinished) {
            finishAnimation()
        }
    }, [hideStartAnimation, hasQuizOverlay, activity?.id])

    useEffect(() => {
        if (!activity) {
            return
        }
        if (animationFinished) {
            startChrono()
        } else {
            if (time === 0 && !seenLottie && !hasQuizOverlay) {
                PlaySoundM.play(
                    require("../../../../assets/sounds/game-start-cut.mp3")
                )
            }
            if (seenLottie) {
                setTimeout(() => {
                    if (activity?.duration > 0) {
                        setAnimationFinished(true)
                    } else {
                        setSeenLottie(false)
                    }
                }, 2000)
            }
        }
    }, [activity?.id, animationFinished, seenLottie])

    // ** App state to detect if the app goes to background
    const appState = useRef(AppState.currentState)

    // ** Form field
    const { field } = useController({
        control,
        name: "activity_logs",
    })

    useEffect(() => {
        // Add event listener in case the app goes to background
        const sub = AppState.addEventListener("change", handleAppStateChange)
        return () => sub.remove()
    }, [])

    useEffect(() => {
        if (
            status === "finished" &&
            time > activity?.estimated_duration * 60 * 1.05
        ) {
            // We allow 5% more time than estimated
            // If timer is more than estimated duration
            setChronoStepFinishedComment(
                "Tu as dépassé la durée maximum prévue pour cette activité"
            )
        }
    }, [status])

    const handleAppStateChange = async (nextAppState) => {
        if (
            appState?.current === "active" &&
            nextAppState.match(/inactive|background/) &&
            statusRef?.current === "ongoing"
        ) {
            recordStartTime()
        }

        if (
            appState?.current.match(/inactive|background/) &&
            nextAppState === "active" &&
            statusRef?.current === "ongoing"
        ) {
            // We just became active again: recalculate elapsed time based on what we stored in AsyncStorage when we started.
            const elapsed = await getElapsedTime()
            // Update the elapsed seconds state
            setTime(time + elapsed)
        }
        appState.current = nextAppState
    }

    const dimensions = useContext(DimensionsContext)

    const startChrono = () => {
        setStatus("ongoing")
    }

    // ** Update chrono every 30 seconds
    useInterval(() => {
        if (activity?.id) {
            props.apiRequest(
                activitiesService.updateChrono,
                { id: activity?.id },
                {
                    chrono_duration: time,
                    live_mode: !!props.liveSessionId,
                    live_session_id: props.liveSessionId ?? undefined,
                }
            )
        }
    }, 30000)

    const isSoundMode = hasRole(props.user, "student") && props.soundModeIds?.includes(props.user?.id)
    const isGroupLive = props.currentLiveSession?.live_room?.type !== "individual" && props.currentLiveSession?.live_room?.type !== "trial"

    return (
        <View
            style={{
                flex: 1,
                width: dimensions?.width,
                alignItems: "center",
                alignContent: "flex-start",
                justifyContent: "flex-start",
                paddingHorizontal: (dimensions?.width ?? 0) > 700 ? 15 : 0,
            }}
        >
            <OneStepActivityHeader
                activity={activity}
                liveMode={props.isLiveModeEnabled}
                time={time}
                estimatedDuration={activity?.estimated_duration * 60}
                field={field}
                recordStartTime={recordStartTime}
                setTime={setTime}
                startChrono={startChrono}
                status={status}
                setStatus={setStatus}
                activityPaused={props.activityPaused || (isGroupLive && isSoundMode) || props.hasQuizOverlay}
                onExit={onExit}
                route={route}
                hideActivityMenuButton={props.hideActivityMenuButton}
                isInCheckpointOverlay={isInCheckpointOverlay}
            />

            {!animationFinished && (
                <View
                    style={{
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {!seenLottie ? (
                        <>
                            <LottieView
                                autoPlay
                                speed={1}
                                loop={false}
                                pointerEvents="none"
                                style={{
                                    width:
                                        Platform.OS !== "web"
                                            ? dimensions?.width * 1.4
                                            : dimensions?.width,
                                    alignSelf: "center",
                                    height:
                                        Dimensions.get("window").height * 0.8,
                                }}
                                onLayout={() => {
                                    // In case animation never finishes, we set a timeout to start the chrono
                                    setTimeout(() => {
                                        finishAnimation()
                                    }, 6000)
                                }}
                                onAnimationFinish={() => {
                                    finishAnimation()
                                }}
                                source={require("../../../../assets/animations/Countdown.json")}
                            />
                        </>
                    ) : (
                        <TextM
                            fontWeight={"ExtraBold"}
                            fontSize={24}
                            color={theme.colors.primary}
                            style={{ textAlign: "center" }}
                            wrap
                        >
                            {time === 0
                                ? "Ton activité va commencer..."
                                : "Ton activité va reprendre..."}
                        </TextM>
                    )}
                    <FilledButtonM
                        mode={"text"}
                        label="Passer l'animation"
                        style={{
                            alignSelf: "center",
                            position: "absolute",
                            bottom: 40,
                        }}
                        labelStyle={{ fontSize: 12 }}
                        color={theme.colors.primary}
                        onPress={() => {
                            finishAnimation()
                        }}
                    />
                </View>
            )}

            {submitted ? (
                <View
                    style={{
                        flex: 1,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {globalProgress ? (
                        IS_WEB ? (
                            <TextM
                                color={theme.colors.success}
                                fontWeight={"Bold"}
                            >
                                {globalProgress + "%"}
                            </TextM>
                        ) : (
                            <CircularProgress
                                title={globalProgress + "%"}
                                value={globalProgress}
                                radius={40}
                                activeStrokeColor={theme.colors.success}
                                inActiveStrokeColor={theme.colors.success}
                                activeStrokeWidth={10}
                                inActiveStrokeWidth={10}
                                inActiveStrokeOpacity={0.1}
                                textColor={theme.colors.success}
                                titleStyle={{ fontFamily: "Montserrat-Bold" }}
                                titleFontSize={16}
                                showProgressValue={false}
                            />
                        )
                    ) : (
                        <ActivityIndicator
                            animating={true}
                            style={{
                                alignSelf: "center",
                                justifySelf: "center",
                                marginTop: IS_WEB ? 50 : -50,
                                marginBottom: 10,
                            }}
                            size="large"
                            color={theme.colors.primary}
                        />
                    )}
                    <TextM
                        fontWeight="Bold"
                        color={theme.colors.grey}
                        style={{
                            marginTop: 10,
                        }}
                        fontSize={22}
                    >
                        Chargement en cours...
                    </TextM>
                    {!!submittedMessage && submittedMessage !== "" && (
                        <TextM
                            fontWeight="Regular"
                            color={theme.colors.light}
                            style={{
                                marginTop: 5,
                            }}
                            fontSize={16}
                        >
                            {submittedMessage}
                        </TextM>
                    )}
                </View>
            ) : (
                <>
                    {animationFinished && !activityMaterial && (
                        <ChampionBubble
                            name={"abou"}
                            variant={"main"}
                            championPosition="left"
                            texts={[stepDescriptions[0]]}
                            style={{}}
                        />
                    )}
                    {activityMaterial && animationFinished ? (
                        <MaterialModal
                            handleShowModal={handleShowModal}
                            isOpen={isOpen}
                            material={activityMaterial}
                            time={time}
                            estimatedDuration={
                                activity?.estimated_duration * 60
                            }
                            startChrono={startChrono}
                            noModal
                            status={status}
                        />
                    ) : animationFinished ? (
                        <View
                            style={{
                                alignItems: "center",
                                justifyContent: "center",
                                marginTop: 20,
                            }}
                        >
                            <FilledButtonM
                                mode={"text"}
                                label={"Ajouter " + uMaterialSlug}
                                color={theme.colors.primary}
                                onPress={() => {
                                    navigation.push("new-material", {
                                        activityId: activity?.id,
                                    })
                                }}
                                icon={"plus"}
                                contentStyle={{
                                    flexDirection: "column",
                                    marginTop: 10,
                                }}
                            />
                        </View>
                    ) : null}
                </>
            )}
            <MaterialModal
                handleShowModal={handleShowModal}
                isOpen={isOpen}
                material={activityMaterial}
                time={time}
                estimatedDuration={activity?.estimated_duration * 60}
                status={status}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        isOfflineModeValid: state.offlineMode.isActivityOfflineModeValid,
        isLiveModeEnabled: state.liveMode.isLiveModeEnabled,
        startedWithOfflineMode:
            state.offlineMode.activityStartedWithOfflineMode,
        isInternetReachable: state.offlineMode.isInternetReachable,
        isOfflineModeEnabled: state.offlineMode.isOfflineModeEnabled,
        timerActivityStepOngoing: state.offlineMode?.timerStepOngoing,
        activityTemplates: state.data.staticData?.activity_templates,
        activityPaused: state.activity?.activityPaused,
        liveSessionId: state.liveMode?.liveSessionId,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        chrono: state.data.currentUser?.object?.chrono,
        user: state.data.currentUser?.object,
        timers: state.activity?.timers,
        soundModeIds: state.liveMode?.soundModeIds,
    }
}

const mapDispatchToProps = {
    activityStartedWithOfflineMode,
    activityOfflineModeInvalid,
    setIsInternetReachable,
    setIsOfflineModeEnabled,
    timerStepStarted,
    apiRequest,
    apiIdle,
    pauseActivity,
    setTimerState,
}

export default connect(mapStateToProps, mapDispatchToProps)(TimerActivityStep)
